/**
 * `/__ui/board` — the table view running on the in-memory fixture source, with no api, no
 * database and no session (spec M1-B4 §5, brief "runs without the backend").
 *
 * Parity card `P-002`: the page used to paint two grey rectangles where the top bar and the
 * sidebar belong. A screen fixture that is missing the screen's chrome proves nothing about
 * the screen, so the route now renders **inside the real `<AppShell/>`** — top bar, 72 px
 * rail, 279 px pane, board header — with the three shell queries answered from
 * `modules/shell/demo.tsx` instead of tRPC. The board region therefore lands at the same
 * coordinates the product puts it at, which is what makes the pixel diff meaningful.
 *
 * Query parameters let a Playwright test open a specific state directly:
 *   `?pane=quick-filter|advanced-filter|sort|hide|group-by|person|search|add-column`
 *   `?scroll=<px>` `?collapse=<groupId>` `?search=<term>` `?groupBy=<columnId>`
 *   `?select=<n>` selects the first n rows, `?focus=<itemId>:<columnId>` focuses one cell
 *
 * Round 5 adds the item card and the board log to the same harness, seeded from the sandbox
 * item the capture was taken from (`modules/item-card/fixture.ts`):
 *   `?card=1` opens the card, `?tab=updates|files|activity|info` picks a tab,
 *   `?card=empty` opens it with no updates, `?log=1` opens the board activity panel.
 *
 * Round 11j adds `?card=subitems404` and `?subitems=404` (**P-308**): every subitems read and `create_subitem` is
 * refused with the api's own sentence, and the board must still render in full.
 *
 * Round 11p adds `?parent=imported` (**P-326**): the parent board without the `status` / `person`
 * columns the subitems board also has — an imported board's columns carry their own ids — so a
 * subitem cell can only work if it is resolved against the **subitems** board.
 */
import type { ColumnId, GroupId, ItemId, ViewId } from '@elizmonday/shared';
import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router';
import {
  fixtureSeed,
  FIXTURE_BOARD_ID,
  FIXTURE_BOARD_NAME,
  FIXTURE_VIEW_ID,
} from '../../modules/board-view/data/fixture';
import { InMemoryBoardDataSource } from '../../modules/board-view/data/memory';
import { TableViewProvider, useTableContext } from '../../modules/board-view/table/context';
import { TableView } from '../../modules/board-view/table/TableView';
import { BoardActivityPanel } from '../../modules/item-card/BoardActivityPanel';
import { ItemCard, type ItemCardTab } from '../../modules/item-card/ItemCard';
import { createFixtureItemCardSource } from '../../modules/item-card/fixture';
import { extendTo, focusCell, selectItems } from '../../modules/board-view/table/selection';
import type { PaneKind } from '../../modules/board-view/table/store';
import './harness.css';

export function BoardHarness() {
  const [params] = useSearchParams();
  const viewId = (params.get('view') ?? FIXTURE_VIEW_ID) as ViewId;
  /**
   * **P-308** — `?subitems=404` opens the board whose subitems board answers a refusal, the
   * pilot's shape (a duplicate pointing at another board's subitems board, P-307). The board
   * itself still answers: the verifier checks that 100 % of the rows render, that expanding
   * shows the muted line, and that `+ Add subitem` answers in the toast rather than the route.
   */
  const subitemsError = params.get('subitems') === '404' ? 'Board 6061227186 not found' : undefined;
  const imported = params.get('parent') === 'imported';
  const source = useMemo(() => {
    const seed = fixtureSeed(subitemsError === undefined ? {} : { subitemsError });
    if (!imported) return new InMemoryBoardDataSource(seed);
    // P-326: the subitems board's own column ids, absent from the parent board.
    const shared = new Set(seed.subitemColumns?.map((column) => String(column.id)) ?? []);
    shared.delete('name');
    return new InMemoryBoardDataSource({
      ...seed,
      columns: seed.columns.filter((column) => !shared.has(String(column.id))),
    });
  }, [subitemsError, imported]);

  const card = params.get('card');
  const log = params.get('log');
  const tab = (params.get('tab') ?? 'updates') as ItemCardTab;
  const cardSource = useMemo(
    () =>
      createFixtureItemCardSource({
        empty: card === 'empty',
        boardId: FIXTURE_BOARD_ID,
        /* P-308: `?card=subitems404` refuses the card's subitems read the way the pilot did. */
        ...(card === 'subitems404' || subitemsError !== undefined
          ? { subitemsError: 'Board 6061227186 not found' }
          : {}),
      }),
    [card, subitemsError],
  );
  const [cardOpen, setCardOpen] = useState(card !== null);
  const [logOpen, setLogOpen] = useState(log !== null);

  return (
    <div className="ez-board-host" data-testid="board-harness" data-log-open={logOpen || undefined}>
      <TableViewProvider source={source} boardId={FIXTURE_BOARD_ID} viewId={viewId}>
        <HarnessState />
        <TableView activeViewId={viewId} />
      </TableViewProvider>
      {cardOpen ? (
        <ItemCard source={cardSource} itemId="F-ITEM-1" initialTab={tab} onClose={() => setCardOpen(false)} />
      ) : null}
      {logOpen ? (
        <BoardActivityPanel
          source={cardSource}
          boardId={FIXTURE_BOARD_ID}
          boardName={FIXTURE_BOARD_NAME}
          onClose={() => setLogOpen(false)}
        />
      ) : null}
    </div>
  );
}

/** Applies the query parameters once the first load has resolved. */
function HarnessState() {
  const { store } = useTableContext();
  const [params] = useSearchParams();
  const [applied, setApplied] = useState(false);

  useEffect(() => {
    if (applied) return;
    const unsubscribe = store.subscribe((state) => {
      if (state.status !== 'ready') return;
      unsubscribe();
      setApplied(true);
      void apply(store, params);
    });
    if (store.getState().status === 'ready') {
      unsubscribe();
      setApplied(true);
      void apply(store, params);
    }
    return unsubscribe;
  }, [store, params, applied]);

  return null;
}

async function apply(
  store: ReturnType<typeof useTableContext>['store'],
  params: URLSearchParams,
): Promise<void> {
  const state = store.getState();

  const search = params.get('search');
  const groupBy = params.get('groupBy');
  if (search || groupBy) {
    state.setEffective({
      ...(search ? { search } : {}),
      ...(groupBy
        ? { groupBy: { columnId: groupBy as ColumnId, direction: 'asc' as const, showEmptyGroups: false } }
        : {}),
    });
    await waitForReload();
  }

  const collapse = params.get('collapse');
  if (collapse) {
    await state.toggleGroupCollapsed(collapse as GroupId, true);
  }

  const select = Number(params.get('select') ?? '0');
  if (select > 0) {
    const rows = store
      .getState()
      .payload?.groups.flatMap((group) => group.page.items)
      .slice(0, select)
      .map((item) => item.id) as ItemId[] | undefined;
    if (rows) store.getState().setItemSelection(selectItems(rows, rows));
  }

  const focus = params.get('focus');
  if (focus) {
    const [itemId, columnId] = focus.split(':') as [ItemId, ColumnId];
    const geometry = {
      rows: (store.getState().payload?.groups ?? []).flatMap((group) =>
        group.page.items.map((item) => ({ itemId: item.id, groupId: group.id as string })),
      ),
      columnIds: (store.getState().payload?.columns ?? []).map((column) => column.id),
    };
    store.getState().setSelection(focusCell(geometry, { itemId, columnId }));
  }

  const range = params.get('range');
  if (range) {
    // `range=<itemA>:<colA>..<itemB>:<colB>` — used by the copy/paste screenshots.
    const [from, to] = range.split('..');
    const [a1, a2] = (from ?? '').split(':') as [ItemId, ColumnId];
    const [b1, b2] = (to ?? '').split(':') as [ItemId, ColumnId];
    const payload = store.getState().payload;
    const rows = (payload?.groups ?? []).flatMap((group) =>
      group.page.items.map((item) => ({ itemId: item.id, groupId: group.id as string })),
    );
    const geometry = { rows, columnIds: (payload?.columns ?? []).map((column) => column.id) };
    const start = focusCell(geometry, { itemId: a1, columnId: a2 });
    store.getState().setSelection(extendTo(geometry, start, { itemId: b1, columnId: b2 }));
  }

  const pane = params.get('pane');
  if (pane) store.getState().openPane(pane as PaneKind);

  const scroll = Number(params.get('scroll') ?? '0');
  if (scroll > 0) {
    requestAnimationFrame(() => {
      document.querySelector('.ez-grid__scroller')?.scrollTo({ top: scroll });
    });
  }
}

function waitForReload(): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, 0));
}
